import React from "react";
import { Image, Tooltip } from "antd";
import { isCustomerLoggedIn } from "../../../../helpers/bycrypt";
import placeHolderImage from "../../../../Assets/Images/placeholderImage.png";
import { formatCurrency, getFullAwsUrl } from "../../../../helpers/utils";
import { svg } from "../../../../Assets/svg";
import { RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT } from "../quotationTypes";

interface BespokeItemsDetailsProps {
  data: any;
}

const BespokeItemsDetails: React.FC<BespokeItemsDetailsProps> = ({ data }) => {
  if (!data) return null;

  const bespokeList = data.bespoke_items || [];
  const isRetailPriceVisible =
    !isCustomerLoggedIn() ||
    data?.customer_snapshot?.show_retail_price !== false;
  const showHiddenRetailPriceTooltip =
    !isCustomerLoggedIn() &&
    data?.customer_snapshot?.show_retail_price === false;

  const quotationMarginVal = Number(
    data?.margin !== undefined && data?.margin !== null && data?.margin !== ""
      ? data.margin
      : data?.customer_snapshot?.margin !== undefined &&
        data?.customer_snapshot?.margin !== null &&
        data?.customer_snapshot?.margin !== ""
      ? data.customer_snapshot.margin
      : (data?.customer_id as any)?.margin || 0,
  );

  const quotationDiscountVal = Number(
    data?.discount !== undefined &&
      data?.discount !== null &&
      data?.discount !== ""
      ? data.discount
      : data?.customer_snapshot?.discount || 0,
  );

  const calculatedTotal = bespokeList.reduce((acc: number, item: any) => {
    const costVal = Number(item.cost_price) || 0;
    const itemMkVal = Number(item.markup) || 0;
    const qtyVal = Number(item.qty) || 1;
    const costAfterItemMk = costVal * (1 + itemMkVal / 100);
    const unitPrice = Number(
      (costAfterItemMk * (1 + quotationMarginVal / 100)).toFixed(2),
    );
    const basicTotal = Number((unitPrice * qtyVal).toFixed(2));
    const discountAmount = Number(
      ((basicTotal * quotationDiscountVal) / 100).toFixed(2),
    );
    return acc + Number((basicTotal - discountAmount).toFixed(2));
  }, 0);

  return (
    <div>
      {/* Section Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="fs-5 fw-bold text-black mb-0">
          Bespoke / Bought-In Items
        </h4>
      </div>

      <div className="card border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white">
        {bespokeList.length > 0 ? (
          <div className="d-flex flex-column gap-3">
            {bespokeList.map((item: any, index: number) => {
              const key = item._id || item.bespoke_item_id || index;
              const costVal = Number(item.cost_price) || 0;
              const itemMkVal = Number(item.markup) || 0;
              const qtyVal = Number(item.qty) || 1;

              const costAfterItemMk = costVal * (1 + itemMkVal / 100);
              const unitPrice = Number(
                (costAfterItemMk * (1 + quotationMarginVal / 100)).toFixed(2),
              );
              const basicTotal = Number((unitPrice * qtyVal).toFixed(2));
              const discountAmount = Number(
                ((basicTotal * quotationDiscountVal) / 100).toFixed(2),
              );
              const finalTotal = Number(
                (basicTotal - discountAmount).toFixed(2),
              );

              const itemImage = item.image
                ? getFullAwsUrl(item.image)
                : placeHolderImage;

              return (
                <div key={key} className="card border shadow-sm rounded-3">
                  <div className="card-body py-3 px-3 fs-6 fw-semibold text-dark">
                    <div className="row gy-sm-3 gy-2 align-items-lg-center">
                      <div className="col-12">
                        <div className="d-flex gap-md-3 gap-2">
                          <div className="img-60 flex-shrink-0">
                            <Image
                              src={itemImage}
                              alt={item.name}
                              width="50"
                              height="50"
                              preview={false}
                              rootClassName="w-100 h-100"
                              className="rounded-2 object-fit-cover border w-100 h-100"
                              fallback={placeHolderImage}
                            />
                          </div>
                          <div className="d-flex flex-column justify-content-center gap-1 w-100">
                            <div className="d-flex justify-content-between align-items-start gap-2">
                              <h6 className="fw-bold mb-0 text-dark fs-6">
                                {item.name || "Bespoke Item"}
                              </h6>
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                {item.supplier_name && (
                                  <span className="badge bg-light text-dark border px-2 py-1 fs-7 fw-normal">
                                    Supplier: {item.supplier_name}
                                  </span>
                                )}
                                {item.supplier_quote_no && (
                                  <span className="badge bg-light text-secondary border px-2 py-1 fs-7 fw-normal">
                                    Quote No: {item.supplier_quote_no}
                                  </span>
                                )}
                                {item.supplier_cost_price !== undefined &&
                                  item.supplier_currency && (
                                    <span className="badge bg-light text-dark border px-2 py-1 fs-7 fw-normal">
                                      Supplier Price:{" "}
                                      {formatCurrency(
                                        item.supplier_cost_price,
                                        item.supplier_currency,
                                        1,
                                      )}
                                    </span>
                                  )}
                              </div>
                            </div>

                            {item.description && (
                              <small
                                className="text-muted fw-normal text-wrap d-block lh-tight mt-1"
                                style={{ fontSize: "0.85rem" }}
                              >
                                {item.description}
                              </small>
                            )}

                            {/* Process Instructions */}
                            {(item.process_instructions || item.bespoke_item_id?.process_instructions) && (
                              <>
                                <hr className="my-2 text-muted opacity-25" />
                                <p
                                  className="mb-0 text-dark fw-semibold"
                                  style={{ fontSize: "0.9rem", lineHeight: 1.5, whiteSpace: "pre-wrap" }}
                                >
                                  {item.process_instructions || item.bespoke_item_id?.process_instructions}
                                </p>
                              </>
                            )}

                            {/* Product-style Metrics Row */}
                            <div className="row gy-2 text-start text-md-start mt-2">
                              {isRetailPriceVisible && (
                                <div className="col-lg-2 col-md-3 col-sm-4 col-6">
                                  <div className="d-flex align-items-start gap-1">
                                    <div>
                                      <small className="text-muted d-block fw-normal">
                                        Retail(RRP):
                                      </small>
                                      <span className="fw-bold">
                                        {formatCurrency(
                                          unitPrice,
                                          data?.currency,
                                          data?.conversion_rate || 1,
                                        )}
                                      </span>
                                    </div>
                                    {showHiddenRetailPriceTooltip && (
                                      <Tooltip
                                        title={RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT}
                                      >
                                        <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                          {svg?.restrictedAvatarIcon}
                                        </span>
                                      </Tooltip>
                                    )}
                                  </div>
                                </div>
                              )}


                              {isRetailPriceVisible && (
                                <div className="col-lg-2 col-md-3 col-sm-4 col-6">
                                  <div className="d-flex align-items-start gap-1">
                                    <div>
                                      <small className="text-muted d-block fw-normal">
                                        Retail(RRP) Total:
                                      </small>
                                      <span className="fw-bold">
                                        {formatCurrency(
                                          basicTotal,
                                          data?.currency,
                                          data?.conversion_rate || 1,
                                        )}
                                      </span>
                                    </div>
                                    {showHiddenRetailPriceTooltip && (
                                      <Tooltip
                                        title={RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT}
                                      >
                                        <span className="svg-16 text-muted cursor-pointer d-inline-flex align-items-center">
                                          {svg?.restrictedAvatarIcon}
                                        </span>
                                      </Tooltip>
                                    )}
                                  </div>
                                </div>
                              )}
                              {isRetailPriceVisible && (
                                <div className="col-lg-2 col-md-3 col-sm-4 col-6">
                                  <div className="d-flex align-items-start gap-1">
                                    <div>
                                      <small className="text-muted d-block fw-normal">
                                        Discount ({quotationDiscountVal}%):
                                      </small>
                                      <span className="fw-bold">
                                        {formatCurrency(
                                          discountAmount,
                                          data?.currency,
                                          data?.conversion_rate || 1,
                                        )}
                                      </span>
                                    </div>
                                    {showHiddenRetailPriceTooltip && (
                                      <Tooltip
                                        title={RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT}
                                      >
                                        <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                          {svg?.restrictedAvatarIcon}
                                        </span>
                                      </Tooltip>
                                    )}
                                  </div>
                                </div>
                              )}
                              {isRetailPriceVisible && (
                                <div className="col-lg-2 col-md-3 col-sm-4 col-12">
                                  <div className="d-flex align-items-start gap-1">
                                    <div>
                                      <small className="text-muted d-block fw-normal">
                                        Total:
                                      </small>
                                      <span className="fw-bold">
                                        {formatCurrency(
                                          finalTotal,
                                          data?.currency,
                                          data?.conversion_rate || 1,
                                        )}
                                      </span>
                                    </div>
                                    {showHiddenRetailPriceTooltip && (
                                      <Tooltip
                                        title={RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT}
                                      >
                                        <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                          {svg?.restrictedAvatarIcon}
                                        </span>
                                      </Tooltip>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="text-end fs-5 mt-2 me-2">
              Bespoke Items Total Amount:{" "}
              <span className="fw-bold text-primary">
                {formatCurrency(
                  calculatedTotal || data?.bespoke_items_total || 0,
                  data?.currency,
                  data?.conversion_rate || 1,
                )}
              </span>
            </div>
          </div>
        ) : (
          <div className="text-center text-muted py-3 fst-italic">
            No bespoke / bought-in items added to this quotation.
          </div>
        )}
      </div>
    </div>
  );
};

export default BespokeItemsDetails;
